import { Boxes, Database, Lock, Users } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { MIN_PASSWORD_LENGTH } from '@hyphen-db-studio/access-core';
import { useAuthStore } from '../store/useAuthStore';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/15 dark:border-white/15 dark:bg-gray-800 dark:text-gray-100';

export default function AuthPage() {
  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(true);

  useEffect(() => {
    fetch('/api/auth/config')
      .then((r) => r.json())
      .then((c: { registrationOpen?: boolean }) => setRegistrationOpen(c.registrationOpen !== false))
      .catch(() => undefined);
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') await login(identifier, password);
      else await register(username, email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  function switchMode(next: 'login' | 'register') {
    setMode(next);
    setError(null);
  }

  return (
    <div className="page-bg flex items-center justify-center px-4 py-10">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-3xl border border-gray-200/80 bg-panel shadow-pop dark:border-white/10 dark:bg-gray-900 md:grid-cols-2">
        <div className="hidden flex-col justify-between bg-gradient-to-br from-sky-600 via-sky-500 to-cyan-400 p-10 text-white md:flex">
          <div className="flex items-center gap-2.5">
            <img src="/logo.svg" alt="" className="h-9 w-9 rounded-lg ring-2 ring-white/40" />
            <span className="text-lg font-semibold tracking-tight">System Studio</span>
          </div>
          <div>
            <h2 className="text-3xl font-bold leading-tight tracking-tight">Design databases and systems, together.</h2>
            <ul className="mt-8 space-y-4 text-sm text-sky-50">
              <li className="flex items-center gap-3">
                <Database className="h-5 w-5 shrink-0" /> ER diagrams with live DBML and SQL
              </li>
              <li className="flex items-center gap-3">
                <Boxes className="h-5 w-5 shrink-0" /> Architecture diagrams for your whole stack
              </li>
              <li className="flex items-center gap-3">
                <Users className="h-5 w-5 shrink-0" /> Organizations with per-member permissions
              </li>
            </ul>
          </div>
          <p className="flex items-center gap-2 text-xs text-sky-100">
            <Lock className="h-3.5 w-3.5" /> Your work is saved to your account.
          </p>
        </div>

        <form onSubmit={submit} className="p-8 sm:p-10">
          <div className="mb-8 flex items-center gap-2.5 md:hidden">
            <img src="/logo.svg" alt="" className="h-8 w-8 rounded-lg" />
            <span className="text-lg font-semibold tracking-tight dark:text-gray-100">System Studio</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight dark:text-white">{mode === 'login' ? 'Welcome back' : 'Create your account'}</h1>
          <p className="mb-6 mt-1 text-sm text-gray-500 dark:text-gray-400">
            {mode === 'login' ? 'Sign in with your username or email.' : 'Pick a username so teammates can find and add you.'}
          </p>

          <div className="space-y-4">
            {mode === 'login' ? (
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Username or email</label>
                <input autoFocus value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" className={inputClass} />
              </div>
            ) : (
              <>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Username</label>
                  <input autoFocus value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" className={inputClass} placeholder="e.g. ada" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className={inputClass} placeholder="you@company.com" />
                </div>
              </>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                className={inputClass}
                placeholder={mode === 'register' ? `At least ${MIN_PASSWORD_LENGTH} characters` : undefined}
              />
            </div>
          </div>

          {error && (
            <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </div>
          )}

          <button type="submit" disabled={busy} className="btn-primary mt-6 w-full py-2.5">
            {busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {mode === 'login' ? (
              registrationOpen ? (
              <>
                New here?{' '}
                <button type="button" onClick={() => switchMode('register')} className="font-medium text-sky-600 hover:underline dark:text-sky-400">
                  Create an account
                </button>
              </>
              ) : (
                <>Sign-ups are closed. Ask an organization owner to set up your account.</>
              )
            ) : (
              <>
                Already have an account?{' '}
                <button type="button" onClick={() => switchMode('login')} className="font-medium text-sky-600 hover:underline dark:text-sky-400">
                  Sign in
                </button>
              </>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}
